// InstanceDashboard.js
import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import PageHeaderWithBreadcrumbs from "../../../../components/PageHeaderWithBreadcrumbs";
import { AuthContext } from "../../../../helpers/AuthContext";
import DataDashboard from "../../../../components/DataDashboard";
import {
  Avatar,
  AvatarGroup,
  Box,
  Typography,
  Tooltip,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Button,
  Alert,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import GroupIcon from "@mui/icons-material/Group";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import BarChartIcon from "@mui/icons-material/BarChart";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkIcon from "@mui/icons-material/Link";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import InfoIcon from "@mui/icons-material/Info";

// ✅ NEW ICONS for the “cube” actions popup
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AddBoxIcon from "@mui/icons-material/AddBox";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TodayIcon from "@mui/icons-material/Today";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import OrderUserAssignment from "../orders/orders-single/order-components/orderUserAssignment/OrderUserAssignment";
import CreateProjectModal from "../orders/create-project-modal/CreateProjectModal";

import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

/* -------------------------------------------------------------------------- */
/* Debounce (no deps)                                                         */
/* -------------------------------------------------------------------------- */
function useDebouncedCallback(fn, delay = 350) {
  const timerRef = useRef(null);
  const fnRef = useRef(fn);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  return useCallback(
    (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay]
  );
}

// --- Lead Source normalization (front-end friendly) ---
// NOTE: upgraded to also read leadAttribution.*Touch shapes
const normalizeSource = (c) => {
  // Preferred: leadAttribution (your new rich structure)
  const la = c?.leadAttribution;
  const fromLA =
    la?.lastTouch?.source ||
    la?.firstTouch?.source ||
    la?.raw?.inbound?.lead_source_type ||
    la?.raw?.inbound?.utm_source ||
    la?.raw?.inbound?.source;

  // Fallbacks: legacy/other shapes
  const raw =
    fromLA ||
    c?.source ||
    c?.leadSource ||
    c?.LeadSource ||
    c?.sourcePlatform ||
    c?.utm_source ||
    c?.utm?.source ||
    c?.meta?.utm?.source ||
    c?.meta?.source ||
    c?.createdFrom ||
    c?.origin;

  if (!raw) return "Unknown";

  if (typeof raw === "object") {
    const guess = raw.name || raw.platform || raw.source || raw.value;
    if (guess) return String(guess);
    return "Unknown";
  }

  return String(raw);
};

const getCustomerSearchText = (c) => {
  const notesText = Array.isArray(c?.customerNotes)
    ? c.customerNotes.map((n) => n?.note || n?.text || n?.message || "").join(" ")
    : "";

  const addrText = Array.isArray(c?.address)
    ? c.address
        .map(
          (a) =>
            `${a?.street || ""} ${a?.city || ""} ${a?.state || ""} ${a?.postal || ""} ${
              a?.country || ""
            }`
        )
        .join(" ")
    : "";

  // make leadAttribution searchable too
  const laText = c?.leadAttribution ? JSON.stringify(c.leadAttribution) : "";

  return [
    c?.businessName,
    c?.emailAddress,
    c?.phoneNumber,
    c?.firstName,
    c?.lastName,
    normalizeSource(c),
    notesText,
    addrText,
    laText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

// Missed opportunity heuristic (tweak freely)
const isMissedOpportunity = ({ customer, orderCount, lastActivity }) => {
  const now = Date.now();
  const staleDays = 14;
  const staleMs = staleDays * 24 * 60 * 60 * 1000;

  const createdAt = customer?.createdAt ? new Date(customer.createdAt).getTime() : 0;
  const lastMs = lastActivity ? lastActivity.getTime() : 0;

  const hasNoOrders = (orderCount || 0) === 0;
  const isOldLead = createdAt && now - createdAt > staleMs;
  const isStale = lastMs && now - lastMs > staleMs;

  return hasNoOrders && (isOldLead || isStale);
};

function formatAddress(addr) {
  return [addr.street, addr.city, addr.state, addr.postal, addr.country]
    .filter(Boolean)
    .join(", ");
}

// Helper to format relative time
const formatTimeAgo = (date) => {
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return `${seconds} second${seconds !== 1 ? "s" : ""} ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? "s" : ""} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? "s" : ""} ago`;
};

// --- Source badge styling (pretty + consistent) ---
const SOURCE_BADGE_MAP = {
  facebook: { bg: "#1877F2", fg: "#FFFFFF", label: "Facebook" },
  instagram: { bg: "#C13584", fg: "#FFFFFF", label: "Instagram" },
  x: { bg: "#000000", fg: "#FFFFFF", label: "X" },
  twitter: { bg: "#000000", fg: "#FFFFFF", label: "X" },
  youtube: { bg: "#FF0000", fg: "#FFFFFF", label: "YouTube" },
  linkedin: { bg: "#0A66C2", fg: "#FFFFFF", label: "LinkedIn" },
  tiktok: { bg: "#111111", fg: "#FFFFFF", label: "TikTok" },
  google: { bg: "#4285F4", fg: "#FFFFFF", label: "Google" },
  "google ads": { bg: "#4285F4", fg: "#FFFFFF", label: "Google Ads" },
  adwords: { bg: "#4285F4", fg: "#FFFFFF", label: "Google Ads" },
  bing: { bg: "#008373", fg: "#FFFFFF", label: "Bing" },
  "bing ads": { bg: "#008373", fg: "#FFFFFF", label: "Bing Ads" },
  website: { bg: "#6B7280", fg: "#FFFFFF", label: "Website" },
  web: { bg: "#6B7280", fg: "#FFFFFF", label: "Website" },
  referral: { bg: "#10B981", fg: "#FFFFFF", label: "Referral" },
  email: { bg: "#9333EA", fg: "#FFFFFF", label: "Email" },
  imported: { bg: "#9CA3AF", fg: "#111827", label: "Imported" },
  unknown: { bg: "#E5E7EB", fg: "#111827", label: "Unknown" },
};

const normalizeSourceKey = (source) => {
  const s = String(source || "").trim().toLowerCase();
  if (!s) return "unknown";

  if (s.includes("facebook")) return "facebook";
  if (s.includes("instagram")) return "instagram";
  if (s === "x" || s.includes("twitter")) return "x";
  if (s.includes("youtube")) return "youtube";
  if (s.includes("linkedin")) return "linkedin";
  if (s.includes("tiktok")) return "tiktok";
  if (s.includes("google ads") || s.includes("adwords")) return "google ads";
  if (s.includes("google")) return "google";
  if (s.includes("bing ads")) return "bing ads";
  if (s.includes("bing")) return "bing";
  if (s.includes("website") || s === "web") return "website";
  if (s.includes("referral")) return "referral";
  if (s.includes("email")) return "email";
  if (s.includes("import")) return "imported";

  return s;
};

const getSourceBadgeStyle = (rawSource) => {
  const key = normalizeSourceKey(rawSource);
  const preset = SOURCE_BADGE_MAP[key] || {
    bg: "#111827",
    fg: "#FFFFFF",
    label: rawSource || "Unknown",
  };

  return {
    key,
    bg: preset.bg,
    fg: preset.fg,
    label: preset.label || rawSource || "Unknown",
  };
};

// Helper to pick a customer image URL
const getCustomerAvatar = (c) => {
  const defaultAvatar = "/elite-core-placeholder-avatar.png";
  let logoData = c?.customUploadedBrandLogo;

  if (!logoData) return defaultAvatar;

  if (typeof logoData === "string") {
    try {
      logoData = JSON.parse(logoData);
    } catch {
      // assume it's a raw URL/path
    }
  }

  if (Array.isArray(logoData) && logoData.length) {
    const picked = logoData.find((i) => i.selected) || logoData[0];
    if (picked?.url) {
      return picked.url.startsWith("http")
        ? picked.url
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/${picked.url}`;
    }
    if (typeof picked === "string") {
      return picked.startsWith("http")
        ? picked
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/${picked}`;
    }
  }

  if (typeof logoData === "object" && logoData?.url) {
    return logoData.url.startsWith("http")
      ? logoData.url
      : `${process.env.REACT_APP_NODEJS_SERVER_URL}/${logoData.url}`;
  }

  if (typeof logoData === "string") {
    return logoData.startsWith("http")
      ? logoData
      : `${process.env.REACT_APP_NODEJS_SERVER_URL}/${logoData}`;
  }

  return defaultAvatar;
};

function CustomersAvatarGrid({ customers }) {
  if (!Array.isArray(customers) || customers.length === 0) return null;

  return (
    <Box mb={4}>
      <Typography variant="subtitle1" gutterBottom>
        Verified customers
      </Typography>
      <AvatarGroup max={8}>
        {customers.slice(-8).map((c) => {
          const name = c.businessName || c.emailAddress || `Lead #${c.id}`;
          return (
            <Tooltip key={c.id} title={name} arrow>
              <Avatar src={getCustomerAvatar(c)} alt={name} />
            </Tooltip>
          );
        })}
      </AvatarGroup>
    </Box>
  );
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// -----------------------------------------------------------------------------
// Lead Details Dialog (Attribution) — used by Source badge click + info icon
// -----------------------------------------------------------------------------
function LeadDetailsDialog({ open, onClose, customer, onOpenCustomer, onAssignLead }) {
  const la = customer?.leadAttribution;

  const renderKV = (obj) => {
    const entries = Object.entries(obj || {});
    if (!entries.length) {
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          —
        </Typography>
      );
    }
    return entries.map(([k, v]) => {
      const value =
        v == null ? "" : typeof v === "object" ? JSON.stringify(v, null, 2) : String(v);

      return (
        <Box key={k} sx={{ display: "flex", gap: 1, mb: 0.75 }}>
          <Typography sx={{ fontWeight: 800, minWidth: 140 }}>{k}:</Typography>
          <Typography sx={{ color: "text.secondary", wordBreak: "break-word", flex: 1 }}>
            {value}
          </Typography>
        </Box>
      );
    });
  };

  // helpful derived labels
  const inbound = la?.raw?.inbound || {};
  const firstTouch = la?.firstTouch || {};
  const lastTouch = la?.lastTouch || {};

  const landing =
    lastTouch?.landingPage ||
    firstTouch?.landingPage ||
    inbound?.page_url ||
    inbound?.referrer_url ||
    "";

  const referrer = lastTouch?.referrer || firstTouch?.referrer || inbound?.referrer_url || "";

  const medium = lastTouch?.medium || firstTouch?.medium || inbound?.lead_source_type || "";

  const source = lastTouch?.source || firstTouch?.source || normalizeSource(customer);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 900 }}>
        Lead Details —{" "}
        {customer?.businessName || customer?.emailAddress || `Lead #${customer?.id || ""}`}
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#fff" }}>
        {/* Attribution */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Lead Attribution
          </Typography>

          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 2,
              p: 2,
              background: "linear-gradient(135deg, rgba(195,23,29,0.06), rgba(0,0,0,0.01))",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>First Touch</Typography>
                {renderKV(firstTouch)}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Last Touch</Typography>
                {renderKV(lastTouch)}
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed rgba(0,0,0,0.15)" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <strong>Source:</strong> {source || "—"} &nbsp;•&nbsp; <strong>Medium:</strong>{" "}
                {medium || "—"}
              </Typography>
              {landing ? (
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  <strong>Landing:</strong> {landing}
                </Typography>
              ) : null}
              {referrer ? (
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  <strong>Referrer:</strong> {referrer}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>

        {/* Inbound */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Submitted Form Data
          </Typography>

          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 2,
              p: 2,
              background: "#f8fafc",
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {renderKV(inbound)}
          </Box>
        </Box>

        {/* Customer Summary */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Customer Summary
          </Typography>

          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 2,
              p: 2,
              background: "#fff",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar src={getCustomerAvatar(customer)} />
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>
                      {customer?.businessName || "—"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {customer?.emailAddress || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <strong>Phone:</strong> {customer?.phoneNumber || "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong>{" "}
                  {customer?.createdAt ? new Date(customer.createdAt).toLocaleString() : "—"}
                </Typography>
                <Typography variant="body2">
                  <strong>Assigned:</strong>{" "}
                  {Array.isArray(customer?.AssignedTo) && customer.AssignedTo.length
                    ? customer.AssignedTo
                        .map((u) => `${u.first_name || ""} ${u.last_name || ""}`.trim())
                        .join(", ")
                    : "—"}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>

        <Button
          variant="outlined"
          onClick={() => onAssignLead?.(customer)}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 999,
            color: "#FFF",
            backgroundColor: "#222",
            "&:hover": { backgroundColor: "#000" },
          }}
          startIcon={<PersonAddAlt1Icon />}
        >
          Assign
        </Button>

        <Button
          variant="contained"
          onClick={() => onOpenCustomer?.(customer?.id)}
          sx={{
            textTransform: "none",
            fontWeight: 900,
            borderRadius: 999,
            backgroundColor: "#222",
            "&:hover": { backgroundColor: "#000" },
          }}
          startIcon={<OpenInNewIcon />}
        >
          Open Customer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* NEW: Lead Action “Cube” Dialog                                           */
/* - 5x5 grid of interactive tiles (clickable)                              */
/* - Create Project is fully wired; Launch PromoStore routes to store flow. */
/* - Create Logo is a base template to hook into AI actions + integrations. */
/* -------------------------------------------------------------------------- */
function LeadActionCubeDialog({
  open,
  onClose,
  customer,
  onAssignLead,
  onOpenCustomer,
  instanceUniqueId,
}) {
  const [view, setView] = useState("grid"); // grid | sendSample | launchStore
  const [createProjectOpen, setCreateProjectOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setView("grid");
      setCreateProjectOpen(false);
    }
  }, [open]);

  const title = customer?.businessName || customer?.emailAddress || `Lead #${customer?.id || ""}`;
  const launchPromoStoreFromQuickAction = () => {
    const customerId = customer?.id ? `&customerId=${encodeURIComponent(String(customer.id))}` : "";
    const url = `/instance/${instanceUniqueId}/sales/promostores?quickAction=launch-promostore${customerId}`;
    window.location.assign(url);
  };

  const openLogoTemplate = () => setView("createLogo");

  const Tile = ({ label, icon, onClick, disabled, tone = "dark" }) => {
    const bg =
      tone === "primary"
        ? "linear-gradient(135deg, rgba(195,23,29,0.14), rgba(0,0,0,0.02))"
        : tone === "success"
        ? "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(0,0,0,0.02))"
        : tone === "info"
        ? "linear-gradient(135deg, rgba(59,130,246,0.18), rgba(0,0,0,0.02))"
        : "linear-gradient(135deg, rgba(17,24,39,0.10), rgba(0,0,0,0.02))";

    return (
      <Box
        onClick={disabled ? undefined : onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") onClick?.();
        }}
        sx={{
          height: 78,
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.10)",
          background: bg,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.45 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 0.5,
          transition: "transform 120ms ease, box-shadow 120ms ease",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          "&:hover": disabled
            ? {}
            : {
                transform: "translateY(-2px)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.10)",
              },
          userSelect: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 900,
            textAlign: "center",
            px: 1,
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>
      </Box>
    );
  };

  const PlaceholderForm = ({ heading, subtitle }) => (
    <Box sx={{ p: 1 }}>
      <Box
        sx={{
          border: "1px solid rgba(0,0,0,0.10)",
          borderRadius: 2,
          p: 2,
          background: "#fff",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
          {heading}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {subtitle}
        </Typography>

        <Box
          sx={{
            border: "1px dashed rgba(0,0,0,0.20)",
            borderRadius: 2,
            p: 2,
            background: "linear-gradient(135deg, rgba(195,23,29,0.06), rgba(0,0,0,0.01))",
          }}
        >
          <Typography sx={{ fontWeight: 800 }}>Form placeholder</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            We’ll build this form next (fields + submit + backend wiring).
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
        <RocketLaunchIcon />
        Quick Actions — {title}
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#fff" }}>
        {view === "grid" && (
          <Box>
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "linear-gradient(135deg, rgba(195,23,29,0.08), rgba(0,0,0,0.02))",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar src={getCustomerAvatar(customer)} />
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>{customer?.businessName || "—"}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {customer?.emailAddress || "—"}{" "}
                    {customer?.phoneNumber ? `• ${customer.phoneNumber}` : ""}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  sx={{ textTransform: "none", borderRadius: 999 }}
                  startIcon={<OpenInNewIcon />}
                  onClick={() => onOpenCustomer?.(customer?.id)}
                >
                  Open Customer
                </Button>

                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    backgroundColor: "#222",
                    "&:hover": { backgroundColor: "#000" },
                  }}
                  startIcon={<PersonAddAlt1Icon />}
                  onClick={() => onAssignLead?.(customer)}
                >
                  Assign
                </Button>
              </Box>
            </Box>

            {/* 5x5 “cube grid” */}
            <Box
              sx={{
                borderRadius: 3,
                p: 2,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "radial-gradient(circle at 20% 20%, rgba(195,23,29,0.10), rgba(255,255,255,0.95))",
              }}
            >
              <Typography sx={{ fontWeight: 900, mb: 1 }}>
                Action Cube (5×5)
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                Click a tile to kick off a workflow for this lead.
              </Typography>

              <Grid container spacing={1.2}>
                {/* Row 1 */}
                <Grid item xs={12} sm={2.4}>
                  <Tile
                    label="Create Project"
                    icon={<AddBoxIcon />}
                    tone="primary"
                    onClick={() => setCreateProjectOpen(true)}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile
                    label="Send Sample"
                    icon={<Inventory2Icon />}
                    tone="success"
                    onClick={() => setView("sendSample")}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile
                    label="Launch PromoStore"
                    icon={<StorefrontIcon />}
                    tone="info"
                    onClick={launchPromoStoreFromQuickAction}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Create Logo" icon={<AutoFixHighIcon />} onClick={openLogoTemplate} />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Send Email" icon={<EmailIcon />} disabled />
                </Grid>

                {/* Row 2 */}
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Text Lead" icon={<PhoneIphoneIcon />} disabled />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Attach Files" icon={<InsertDriveFileIcon />} disabled />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Quote" icon={<AttachMoneyIcon />} disabled />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Set Follow-Up" icon={<TodayIcon />} disabled />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <Tile label="Notes" icon={<InfoIcon />} disabled />
                </Grid>

                {/* Rows 3–5 = “future” tiles (disabled placeholders) */}
                {Array.from({ length: 15 }).map((_, idx) => (
                  <Grid item xs={12} sm={2.4} key={`future-${idx}`}>
                    <Tile label="Coming Soon" icon={<RocketLaunchIcon fontSize="small" />} disabled />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}

        {view === "sendSample" && (
          <Box>
            <Button
              variant="outlined"
              onClick={() => setView("grid")}
              sx={{ textTransform: "none", borderRadius: 999, mb: 2 }}
            >
              ← Back to Cube
            </Button>
            <PlaceholderForm
              heading="Send Sample"
              subtitle="This will become the Send Sample workflow (address + item + tracking + email)."
            />
          </Box>
        )}

        {view === "launchStore" && (
          <Box>
            <Button
              variant="outlined"
              onClick={() => setView("grid")}
              sx={{ textTransform: "none", borderRadius: 999, mb: 2 }}
            >
              ← Back to Cube
            </Button>
            <PlaceholderForm
              heading="Launch a Web Store"
              subtitle="This will become the Launch Web Store form (store type, close date, products, invite flow)."
            />
          </Box>
        )}

        {view === "createLogo" && (
          <Box>
            <Button
              variant="outlined"
              onClick={() => setView("grid")}
              sx={{ textTransform: "none", borderRadius: 999, mb: 2 }}
            >
              ← Back to Cube
            </Button>
            <Box
              sx={{
                border: "1px solid rgba(0,0,0,0.10)",
                borderRadius: 2,
                p: 2,
                background: "#fff",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
                Create Logo (Template)
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
                This quick action will generate customer logos from the AI Actions workflow.
                We will route requests through the existing instance integrations setup so credentials are centrally managed.
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Next step: wire this action to trigger a new AI Action flow in <strong>/ai/actions</strong> using your configured integrations.
              </Alert>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant="contained"
                  onClick={() => window.open(`/instance/${instanceUniqueId}/ai/actions`, "_blank")}
                >
                  Open AI Actions
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    window.open(`/instance/${instanceUniqueId}/settings/global-settings/integrations`, "_blank")
                  }
                >
                  Open Integrations
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>

      <CreateProjectModal
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        onCreated={() => {
          setCreateProjectOpen(false);
          onClose?.();
        }}
        customers={customer ? [customer] : []}
        lockedCustomerId={customer?.id || null}
        defaultCustomerId={customer?.id || null}
      />
    </Dialog>
  );
}

/**
 * Server-paged Sales Pipeline cards.
 * - Customers come in already paged (pageSize items)
 * - Local filtering/sorting operates ONLY within the current page
 * - Pagination/footer is controlled by parent (InstanceDashboard)
 */
function CustomerCardsGrid({
  customers = [],
  orders = [],
  onGoCustomers,
  onAssign,
  onViewLead,
  onOpenActions, // ✅ NEW

  // server paging controls (controlled)
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,

  // server search controls (controlled)
  search,
  onSearchChange,
}) {
  const [mode, setMode] = React.useState("lastUpdated"); // newCustomers | lastUpdated | missed
  const [sourceFilter, setSourceFilter] = React.useState("all");

  // ---- FAST: compute orderCounts in ONE pass (not N filters) ----
  const orderCounts = React.useMemo(() => {
    const counts = {};
    for (const o of orders || []) {
      const attached = Array.isArray(o.attachedCustomers) ? o.attachedCustomers : [];
      for (const customerId of attached) {
        counts[customerId] = (counts[customerId] || 0) + 1;
      }
    }
    return counts;
  }, [orders]);

  // compute last activity per customer (memoized)
  const lastActivities = React.useMemo(() => {
    const acc = {};
    for (const c of customers || []) {
      let last = new Date(c.updatedAt || c.createdAt || Date.now());
      if (Array.isArray(c.customerNotes)) {
        for (const note of c.customerNotes) {
          const t = new Date(note.timestamp);
          if (!isNaN(t.getTime()) && t > last) last = t;
        }
      }
      acc[c.id] = last;
    }
    return acc;
  }, [customers]);

  // source options (within current page; OK since server paging)
  const sourceOptions = React.useMemo(() => {
    const set = new Set();
    for (const c of customers || []) set.add(normalizeSource(c));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [customers]);

  // build rows + local filter/sort (current page only)
  const prepared = React.useMemo(() => {
    let rows = (customers || []).map((c) => {
      const count = orderCounts[c.id] || 0;
      const last = lastActivities[c.id] || new Date(c.updatedAt || c.createdAt || Date.now());
      const source = normalizeSource(c);

      const missed = isMissedOpportunity({
        customer: c,
        orderCount: count,
        lastActivity: last,
      });

      return {
        c,
        count,
        last,
        source,
        missed,
        searchText: getCustomerSearchText(c),
      };
    });

    // local source filter (current page only)
    if (sourceFilter !== "all") {
      rows = rows.filter((r) => r.source === sourceFilter);
    }

    // local view modes (current page only)
    if (mode === "newCustomers") {
      rows.sort((a, b) => new Date(b.c.createdAt || 0) - new Date(a.c.createdAt || 0));
    } else if (mode === "missed") {
      rows = rows.filter((r) => r.missed).sort((a, b) => a.last - b.last);
    } else {
      rows.sort((a, b) => b.last - a.last);
    }

    return rows;
  }, [customers, orderCounts, lastActivities, mode, sourceFilter]);

  const startIndex = total ? (page - 1) * pageSize : 0;
  const endIndex = total ? Math.min(startIndex + pageSize, total) : 0;

  if (!Array.isArray(customers) || customers.length === 0) return null;

  return (
    <Box mb={4}>
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          background: "linear-gradient(135deg, rgba(195,23,29,0.08), rgba(0,0,0,0.02))",
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Sales Pipeline
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Welcome to the frontline for sales qualification.
          </Typography>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <TextField
            size="small"
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search leads…"
            sx={{ minWidth: { xs: "100%", sm: 260 }, background: "#fff" }}
          />

          <TextField
            size="small"
            select
            label="View"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            sx={{ minWidth: 180, background: "#fff" }}
          >
            <MenuItem value="newCustomers">New Customers</MenuItem>
            <MenuItem value="lastUpdated">Last Updated</MenuItem>
            <MenuItem value="missed">Missed Opportunities</MenuItem>
          </TextField>

          <TextField
            size="small"
            select
            label="Source"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            sx={{ minWidth: 180, background: "#fff" }}
          >
            {sourceOptions.map((s) => {
              const badge = getSourceBadgeStyle(s);
              return (
                <MenuItem key={s} value={s}>
                  {s === "all" ? "All Sources" : badge.label}
                </MenuItem>
              );
            })}
          </TextField>

          <Button
            variant="outlined"
            onClick={onGoCustomers}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              fontFamily: "Nunito",
              fontWeight: 700,
              borderColor: "#c3171d",
              color: "#c3171d",
              "&:hover": { borderColor: "#9f1217", backgroundColor: "rgba(195,23,29,0.04)" },
            }}
            startIcon={<LinkIcon />}
          >
            Open Customers
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {prepared.map(({ c, count, last, source, missed }) => {
          const relative = formatTimeAgo(last);
          const isRecent = new Date() - last <= 1000 * 60 * 60;

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                  transition: "transform 120ms ease",
                  "&:hover": { transform: "translateY(-2px)" },
                  borderColor: missed ? "rgba(195,23,29,0.45)" : "rgba(0,0,0,0.12)",
                  background: missed ? "#ff303a12" : "",
                }}
              >
                <CardHeader
                  avatar={<Avatar src={getCustomerAvatar(c)} />}
                  title={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {c.businessName || c.emailAddress}
                      </Typography>

                      {/* Source pill (keeps opening attribution dialog) */}
                      {(() => {
                        const badge = getSourceBadgeStyle(source);
                        return (
                          <Box
                            sx={{
                              ml: "auto",
                              fontSize: 11,
                              px: 1,
                              py: 0.35,
                              borderRadius: 999,
                              background: badge.bg,
                              color: badge.fg,
                              fontWeight: 800,
                              maxWidth: 130,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
                              border: "1px solid rgba(255,255,255,0.18)",
                              cursor: "pointer",
                              position: "absolute",
                              bottom: 22,
                              left: 15,
                            }}
                            title={`Source: ${badge.label} (click for details)`}
                            onClick={() => onViewLead?.(c)}
                          >
                            {badge.label}
                          </Box>
                        );
                      })()}
                    </Box>
                  }
                  subheader={
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Projects: {count}
                      {missed ? " • Missed" : ""}
                    </Typography>
                  }
                />

                <CardContent sx={{ pt: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: isRecent ? "success.main" : "text.secondary", mb: 1 }}
                  >
                    Last activity: {relative}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    {/* 🔥 NEW: Quick Actions button opens the cube */}
                    <Tooltip title="Quick actions (cube)" arrow>
                      <IconButton size="small" onClick={() => onOpenActions?.(c)}>
                        <RocketLaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {/*<Tooltip title="View lead attribution details" arrow>
                      <IconButton size="small" onClick={() => onViewLead?.(c)}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>*/}

                    <Tooltip title="Assign lead" arrow>
                      <IconButton size="small" onClick={() => onAssign(c)}>
                        <PersonAddAlt1Icon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Footer pagination (server controlled) */}
      {total > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "#fff",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
            Showing {total ? startIndex + 1 : 0}–{endIndex} of {total}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end" }}>
            <TextField
              size="small"
              select
              label="Per page"
              value={pageSize}
              onChange={(e) => {
                onPageSizeChange?.(Number(e.target.value));
              }}
              sx={{ minWidth: 120, background: "#fff" }}
            >
              {[12, 24, 48, 96].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>

            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, next) => onPageChange?.(next)}
                shape="rounded"
                color="primary"
              />
            </Stack>
          </Box>
        </Box>
      )}

      {!prepared.length && (
        <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: "1px dashed rgba(0,0,0,0.2)" }}>
          <Typography sx={{ fontWeight: 800 }}>No matches on this page</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Try switching View/Source, or broaden search.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const InstanceDashboard = () => {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);

  // Leads (server paged)
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsRpp, setLeadsRpp] = useState(24); // ✅ DEFAULT 24
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsTotalPages, setLeadsTotalPages] = useState(1);

  // ✅ input value (instant) + debounced query (server)
  const [leadsSearchInput, setLeadsSearchInput] = useState("");
  const [leadsQ, setLeadsQ] = useState("");

  // Orders
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [hasLoadedOrders, setHasLoadedOrders] = useState(false);
  const [promoStoreOrders, setPromoStoreOrders] = useState([]);
  const [, setHasLoadedPromoStoreOrders] = useState(false);
  const [promoStoreOrdersLoading, setPromoStoreOrdersLoading] = useState(true);
  const [promoStoreOrdersLimit, setPromoStoreOrdersLimit] = useState("all");
  const [promoStoreOrdersPageSize, setPromoStoreOrdersPageSize] = useState(25);
  const [promoStoreOrdersHours, setPromoStoreOrdersHours] = useState(720);
  const [leaderboardDetail, setLeaderboardDetail] = useState(null);

  const [markers, setMarkers] = useState([]);
  const [failures, setFailures] = useState([]);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCustomer, setAssignCustomer] = useState(null);
  const [assignNote, setAssignNote] = useState("");
  const [notifyAssignee, setNotifyAssignee] = useState(true);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);

  // Attribution dialog
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [leadDetails, setLeadDetails] = useState(null);

  // ✅ NEW: Actions “cube” dialog state
  const [actionCubeOpen, setActionCubeOpen] = useState(false);
  const [actionCubeLead, setActionCubeLead] = useState(null);

  const openCustomerQuick = (customerId) => {
    window.open(
      `/instance/${instanceUniqueId}/sales/customers?tab=all&openCustomer=${customerId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const goToCustomersPage = () => {
    window.open(`/instance/${instanceUniqueId}/sales/customers`, "_blank", "noopener,noreferrer");
  };

  const mapRef = useRef();

  const fetchLeads = async ({ page = leadsPage, rpp = leadsRpp, q = leadsQ } = {}) => {
    setLeadsLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/get-customers`,
        {
          headers: { accessToken: localStorage.getItem("accessToken") },
          params: {
            instanceUniqueId,
            tab: "all",
            page,
            rpp,
            q,
          },
        }
      );

      const list = data.customers || data.Results || [];
      setLeads(list);

      const total = data.total ?? list.length ?? 0;
      setLeadsTotal(total);

      const computedPages = data.totalPages ?? Math.max(1, Math.ceil(total / (rpp || 1)));
      setLeadsTotalPages(computedPages);

      setLeadsPage(data.page ?? page);
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setLeadsLoading(false);
    }
  };

  // ✅ debounced server search
  const debouncedSetServerQuery = useDebouncedCallback((val) => {
    setLeadsPage(1);
    setLeadsQ(val);
  }, 350);

  useEffect(() => {
    setHasLoadedOrders(false);
    setHasLoadedPromoStoreOrders(false);
    setOrders([]);
    setPromoStoreOrders([]);
  }, [instanceUniqueId]);

  // Lazy-load orders only when Project Forecast is opened
  useEffect(() => {
    if (tab !== 2 || hasLoadedOrders) return;

    (async () => {
      setLoading(true);
      try {
        const ordersResp = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-orders`,
          {
            headers: { accessToken: localStorage.getItem("accessToken") },
            params: { instanceUniqueId },
          }
        );
        setOrders(ordersResp.data.Results || []);
        setHasLoadedOrders(true);
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tab, hasLoadedOrders, instanceUniqueId]);

  // Lazy-load PromoStore orders only when PromoStores tab is opened
  useEffect(() => {
    if (tab !== 3) return;

    (async () => {
      setPromoStoreOrdersLoading(true);
      try {
        const promoOrdersResp = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/orders/recent`,
          {
            headers: { accessToken: localStorage.getItem("accessToken") },
            params: {
              instanceUniqueId,
              hours: promoStoreOrdersHours === "all" ? "all" : promoStoreOrdersHours,
              limit: promoStoreOrdersLimit === "all" ? "all" : promoStoreOrdersLimit,
            },
          }
        );
        setPromoStoreOrders(promoOrdersResp.data.orders || []);
        setHasLoadedPromoStoreOrders(true);
      } catch (err) {
        console.error("Error fetching PromoStore orders:", err);
      } finally {
        setPromoStoreOrdersLoading(false);
      }
    })();
  }, [tab, instanceUniqueId, promoStoreOrdersLimit, promoStoreOrdersHours]);

  useEffect(() => {
    if (promoStoreOrdersLimit === "all") {
      setPromoStoreOrdersPageSize(100);
    }
  }, [promoStoreOrdersLimit]);

  // Fetch leads whenever paging/rpp/query changes
  useEffect(() => {
    fetchLeads({ page: leadsPage, rpp: leadsRpp, q: leadsQ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, leadsPage, leadsRpp, leadsQ]);

  useEffect(() => {
    if (!mapRef.current) return;
    setTimeout(() => {
      mapRef.current.invalidateSize();
    }, 0);
  }, [markers]);

  // --- Leads per day (NOTE: current page only) ---
  const leadsByDay = useMemo(() => leads.map((c) => ({
    date: c.createdAt,
    amount: 1,
  })), [leads]);

  const leadsColumns = [
    { field: "date", headerName: "Date", width: 200 },
    { field: "amount", headerName: "Leads", width: 120 },
  ];

  // --- Orders per day ---
  const ordersByDay = useMemo(() => orders.map((o) => ({
    date: o.createdAt,
    amount: 1,
  })), [orders]);

  const resolveOrderStatusGroup = useCallback((order) => {
    const soRaw = order?.OrderStatus?.SO;
    const po = order?.OrderStatus?.PO;
    const soMap = {
      'Sales Order Ready': 'Client Approved',
      Completed: 'Client Approved',
      Rejected: 'Client Change Requested',
      'Sent For Agreement': 'Agreement Pending',
    };
    const so = soMap[soRaw] || soRaw;
    const derived = ['In Production', 'Shipped', 'Ready to be Invoiced', 'Invoiced'];

    if (derived.includes(so)) return so;

    if (so === "Client Approved") return "Client Approved";
    if (po != null) return "PO Stage";
    return so || "Unknown";
  }, []);

  const orderDayColumns = [
    { field: "date", headerName: "Date", width: 200 },
    { field: "amount", headerName: "Orders", width: 120 },
  ];

  const DashboardTabs = [
    {
      key: 0,
      label: "Sales Pipeline",
      icon: <GroupIcon style={{ marginRight: 6, verticalAlign: "middle" }} />,
      tooltip: "Welcome to the frontline for sales qualification.",
    },
    {
      key: 1,
      label: "Lead Forecast",
      icon: <LightbulbIcon style={{ marginRight: 6, verticalAlign: "middle" }} />,
      tooltip: "Leads broken down by day",
    },
    {
      key: 2,
      label: "Project Forecast",
      icon: <BarChartIcon style={{ marginRight: 6, verticalAlign: "middle" }} />,
      tooltip: "Orders and project stages broken down by day and status",
    },
    {
      key: 3,
      label: "PromoStores",
      icon: <StorefrontIcon style={{ marginRight: 6, verticalAlign: "middle" }} />,
      tooltip: "PromoStore orders synced from WordPress in the last 24 hours",
    },
  ];

  // --- Orders by status group ---
  const ordersByStatus = useMemo(() => orders.map((order) => ({
    ...order,
    statusGroup: resolveOrderStatusGroup(order),
  })), [orders, resolveOrderStatusGroup]);

  const orderStatusColumns = [{ field: "statusGroup", headerName: "Status Group", width: 180 }];

  const promoStoreLabelMap = useMemo(() => {
    return promoStoreOrders.reduce((acc, order) => {
      const key = order.promoStoreName || "Unknown";
      if (!acc[key] && order.promoStoreCustomerName) {
        acc[key] = order.promoStoreCustomerName;
      }
      return acc;
    }, {});
  }, [promoStoreOrders]);

  const formatPromoStoreLabel = (value) => {
    if (!value) return "Unknown";
    const key = String(value);
    const customerLabel = promoStoreLabelMap[key];
    if (customerLabel) return customerLabel;

    try {
      const withProtocol = /^https?:\/\//i.test(key) ? key : `https://${key}`;
      const parsed = new URL(withProtocol);
      const path = parsed.pathname.replace(/^\/+|\/+$/g, "");
      return path || parsed.hostname;
    } catch {
      const parts = key.split("/").filter(Boolean);
      return parts[parts.length - 1] || key;
    }
  };

  const resolvePromoStoreUrl = (value) => {
    if (!value) return null;
    const key = String(value);
    if (key === "Unknown") return null;
    return /^https?:\/\//i.test(key) ? key : `https://${key}`;
  };

  const getLogoThumbUrl = (logoData) => {
    if (!logoData) return null;
    let parsed = logoData;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        // leave raw string
      }
    }

    const asUrl = (value) => {
      if (!value || typeof value !== "string") return null;
      return value.startsWith("http")
        ? value
        : `${process.env.REACT_APP_NODEJS_SERVER_URL}/${value}`;
    };

    if (Array.isArray(parsed) && parsed.length) {
      const selected = parsed.find((item) => item?.selected) || parsed[0];
      if (typeof selected === "string") return asUrl(selected);
      return asUrl(selected?.url || selected?.path || selected?.src);
    }

    if (typeof parsed === "object") {
      return asUrl(parsed.url || parsed.path || parsed.src);
    }

    if (typeof parsed === "string") {
      return asUrl(parsed);
    }

    return null;
  };

  const leaderboardRows = useMemo(() => {
    const grouped = promoStoreOrders.reduce((acc, order) => {
      const key = order.promoStoreName || "Unknown";
      if (!acc[key]) {
        acc[key] = {
          key,
          label: promoStoreLabelMap[key] || key,
          url: resolvePromoStoreUrl(key),
          hasCustomerLabel: Boolean(promoStoreLabelMap[key]),
          logo: getLogoThumbUrl(order.promoStoreCustomerLogo),
          count: 0,
          rows: [],
        };
      }
      if (!acc[key].logo) {
        acc[key].logo = getLogoThumbUrl(order.promoStoreCustomerLogo);
      }
      acc[key].count += 1;
      acc[key].rows.push(order);
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => b.count - a.count);
  }, [promoStoreOrders, promoStoreLabelMap]);


  const promoStoreChartValueOptions = useMemo(() => ([
    { key: "order-count", label: "Number of orders", mode: "count" },
    { key: "order-total", label: "Order total", mode: "sum", field: "total" },
  ]), []);

  const promoStoreOrderColumns = [
    { field: "promoStoreName", headerName: "PromoStore", width: 220 },
    { field: "createdBy", headerName: "Created By", width: 160 },
    { field: "wooOrderNumber", headerName: "Woo Order #", width: 140 },
    { field: "status", headerName: "Status", width: 140 },
    {
      field: "total",
      headerName: "Total",
      width: 120,
      valueFormatter: ({ value }) => (value != null ? Number(value).toFixed(2) : "—"),
    },
    { field: "currency", headerName: "Currency", width: 110 },
    { field: "customerName", headerName: "Customer", width: 180 },
    { field: "customerEmail", headerName: "Customer Email", width: 220 },
    {
      field: "dateCreated",
      headerName: "Order Date",
      width: 190,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : "—"),
    },
    {
      field: "adminUrl",
      headerName: "Admin Link",
      width: 140,
      renderCell: ({ value }) =>
        value ? (
          <a href={value} target="_blank" rel="noreferrer">
            View
          </a>
        ) : (
          "—"
        ),
    },
  ];

  const promoStoreOrderRows = useMemo(() =>
    promoStoreOrders.map((order) => ({
      id: order.id || `${order.PromoStoreId}-${order.wooOrderId}`,
      ...order,
    })),
  [promoStoreOrders]);


  // ─── Geocode leads.address into markers & failures (CURRENT PAGE ONLY) ─
  useEffect(() => {
    if (tab !== 0 || !leads.length) return;

    (async () => {
      const good = [];
      const bad = [];

      for (const c of leads) {
        for (const addr of Array.isArray(c.address) ? c.address : []) {
          const q = formatAddress(addr);
          try {
            const { data } = await axios.get("https://nominatim.openstreetmap.org/search", {
              params: { q, format: "json", limit: 1 },
            });

            if (data && data.length) {
              good.push({
                logo: c.customUploadedBrandLogo,
                businessName: c.businessName,
                street: addr.street,
                city: addr.city,
                state: addr.state,
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon),
              });
            } else {
              bad.push(q);
            }

            await new Promise((r) => setTimeout(r, 200));
          } catch {
            bad.push(q);
          }
        }
      }

      setMarkers(good);
      setFailures(bad);
    })();
  }, [leads, tab]);

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <div className="options-container fade-in-effect-usability">
          <div className="options-inner-container">
            <PageHeaderWithBreadcrumbs title="Sales Operations" breadcrumb="Operations" includeButton={false} />

            <div className="tabs customer-tabs">
              {DashboardTabs.map(({ key, label, icon, tooltip }) => (
                <Tooltip key={key} title={tooltip} placement="bottom" arrow>
                  <button className={`tab-btn${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
                    {icon}
                    {label}
                  </button>
                </Tooltip>
              ))}
            </div>

            <TabPanel value={tab} index={0}>
              <CustomerCardsGrid
                customers={leads}
                orders={orders}
                onGoCustomers={() => goToCustomersPage()}
                onAssign={(customer) => {
                  setAssignCustomer(customer);
                  setSelectedAssignees(customer?.AssignedTo || []);
                  setAssignNote("");
                  setNotifyAssignee(true);
                  setAssignOpen(true);
                }}
                onViewLead={(customer) => {
                  setLeadDetails(customer);
                  setLeadDialogOpen(true);
                }}
                // ✅ NEW: open the cube actions dialog
                onOpenActions={(customer) => {
                  setActionCubeLead(customer);
                  setActionCubeOpen(true);
                }}
                // server paging props
                page={leadsPage}
                pageSize={leadsRpp}
                total={leadsTotal}
                totalPages={leadsTotalPages}
                onPageChange={(next) => setLeadsPage(next)}
                onPageSizeChange={(nextRpp) => {
                  setLeadsRpp(nextRpp);
                  setLeadsPage(1);
                }}
                // debounced server search props
                search={leadsSearchInput}
                onSearchChange={(val) => {
                  setLeadsSearchInput(val);
                  debouncedSetServerQuery(val);
                }}
              />
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <DataDashboard
                data={leadsByDay}
                columns={leadsColumns}
                type="Lead Forecast"
                dateFieldName="date"
                groupBy="day"
                defaultChart="bar"
                detailRows={leads}
                detailFields={[
                  { key: "businessName", label: "Business" },
                  { key: "emailAddress", label: "Email" },
                  {
                    key: "createdAt",
                    label: "Created",
                    format: (value) => (value ? new Date(value).toLocaleString() : "—"),
                  },
                ]}
              />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              {loading ? (
                <Typography sx={{ py: 2 }}>Loading project forecast…</Typography>
              ) : (
                <>
                  <DataDashboard
                    data={ordersByDay}
                    columns={orderDayColumns}
                    type="Project Forecast"
                    dateFieldName="date"
                    groupBy="day"
                    defaultChart="bar"
                    detailRows={orders}
                    detailFields={[
                      { key: "customerName", label: "Customer" },
                      { key: "projectName", label: "Project" },
                      {
                        key: "createdAt",
                        label: "Created",
                        format: (value) => (value ? new Date(value).toLocaleString() : "—"),
                      },
                      {
                        key: "statusGroup",
                        label: "Status Group",
                        format: (_value, row) => resolveOrderStatusGroup(row),
                      },
                    ]}
                  />

                  <DataDashboard
                    data={ordersByStatus}
                    columns={orderStatusColumns}
                    type="Project Forecast by Status"
                    groupByField="statusGroup"
                    defaultChart="pie"
                    detailRows={ordersByStatus}
                    detailFields={[
                      { key: "customerName", label: "Customer" },
                      { key: "projectName", label: "Project" },
                      {
                        key: "createdAt",
                        label: "Created",
                        format: (value) => (value ? new Date(value).toLocaleString() : "—"),
                      },
                      { key: "statusGroup", label: "Status Group" },
                    ]}
                  />
                </>
              )}
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Typography sx={{ mb: 2, fontWeight: 600 }}>
                {promoStoreOrdersHours === "all"
                  ? "PromoStore Orders (all time)"
                  : `PromoStore Orders (last ${promoStoreOrdersHours} hours)`}
              </Typography>

              {promoStoreOrdersLoading ? (
                <Typography>Loading PromoStore orders…</Typography>
              ) : (
                <>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
                    <TextField
                      select
                      label="Orders to load"
                      size="small"
                      value={promoStoreOrdersLimit}
                      onChange={(event) => {
                        const value = event.target.value;
                        setPromoStoreOrdersLimit(value === "all" ? "all" : Number(value));
                      }}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value={200}>200</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value="all">All</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Duration"
                      size="small"
                      value={promoStoreOrdersHours}
                      onChange={(event) => {
                        const value = event.target.value;
                        setPromoStoreOrdersHours(value === "all" ? "all" : Number(value));
                      }}
                      sx={{ minWidth: 140 }}
                    >
                      <MenuItem value={24}>24h</MenuItem>
                      <MenuItem value={48}>48h</MenuItem>
                      <MenuItem value={72}>72h</MenuItem>
                      <MenuItem value={168}>7d</MenuItem>
                      <MenuItem value={720}>30d</MenuItem>
                      <MenuItem value="all">All time</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Rows per page"
                      size="small"
                      value={promoStoreOrdersPageSize}
                      onChange={(event) => setPromoStoreOrdersPageSize(Number(event.target.value))}
                      sx={{ minWidth: 160 }}
                    >
                      {[10, 25, 50, 100].map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <DataDashboard
                    data={promoStoreOrders}
                    columns={promoStoreOrderColumns}
                    type={
                      promoStoreOrdersHours === "all"
                        ? "PromoStore Orders (All time)"
                        : `PromoStore Orders (${promoStoreOrdersHours}h)`
                    }
                    groupByField="promoStoreName"
                    defaultChart="bar"
                    labelFormatter={formatPromoStoreLabel}
                    labelLinkResolver={resolvePromoStoreUrl}
                    labelSort="value-asc"
                    detailRows={promoStoreOrders}
                    valueMetricOptions={promoStoreChartValueOptions}
                    defaultValueMetricKey="order-count"
                    detailFields={[
                      { key: "wooOrderNumber", label: "Woo Order #"},
                      { key: "customerName", label: "Customer" },
                      { key: "customerEmail", label: "Email" },
                      { key: "status", label: "Status" },
                      {
                        key: "total",
                        label: "Total",
                        format: (value, row) =>
                          value != null
                            ? `${Number(value).toFixed(2)} ${row.currency || ""}`.trim()
                            : "—",
                      },
                      {
                        key: "dateCreated",
                        label: "Order Date",
                        format: (value) => (value ? new Date(value).toLocaleString() : "—"),
                      },
                      {
                        key: "adminUrl",
                        label: "Admin Link",
                        format: (value) => (value ? value : "—"),
                      },
                    ]}
                  />

                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{ mb: 1, fontWeight: 700 }}>
                      Leaderboard
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      {leaderboardRows.map((row, index) => {
                        const isGold = index === 0;
                        const isSilver = index === 1;
                        const isBronze = index === 2;
                        const baseColor = index % 2 === 0 ? "rgba(0,0,0,0.03)" : "#fff";
                        const background = isGold
                          ? "linear-gradient(90deg, #f7d774 0%, #f5c542 100%)"
                          : isSilver
                          ? "linear-gradient(90deg, #e6e8ed 0%, #cfd3da 100%)"
                          : isBronze
                          ? "linear-gradient(90deg, #f1c4a1 0%, #d89b72 100%)"
                          : baseColor;

                        return (
                          <Box
                            key={row.key}
                            onClick={() => setLeaderboardDetail(row)}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "56px 1fr 120px",
                              alignItems: "center",
                              gap: 2,
                              px: 2,
                              py: 1.5,
                              cursor: "pointer",
                              background,
                              borderBottom: "1px solid rgba(0,0,0,0.06)",
                              "&:hover": { opacity: 0.9 },
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>
                              #{index + 1}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                              {row.logo && (
                                <Avatar
                                  src={row.logo}
                                  alt={row.label}
                                  sx={{ width: 24, height: 24 }}
                                />
                              )}
                              <span>
                                {row.label}
                                {row.hasCustomerLabel && row.url ? ` - (${row.url})` : ""}
                              </span>
                            </Box>
                            <Typography sx={{ textAlign: "right", fontWeight: 700 }}>
                              {row.count} orders
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  <div style={{ marginTop: 24 }}>
                    <Typography sx={{ mb: 1, fontWeight: 600 }}>
                      PromoStore Order Details
                    </Typography>
                    <DataGrid
                      rows={promoStoreOrderRows}
                      columns={promoStoreOrderColumns.map((col) => ({
                        ...col,
                        align: col.align ?? "center",
                        headerAlign: col.headerAlign ?? "center",
                      }))}
                      autoHeight
                      pageSize={Math.min(promoStoreOrdersPageSize, 100)}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      disableSelectionOnClick
                      components={{ Toolbar: GridToolbar }}
                    />
                  </div>
                </>
              )}
            </TabPanel>
          </div>
        </div>
      </div>

      {/* Attribution (source bubble) dialog */}
      <LeadDetailsDialog
        open={leadDialogOpen}
        onClose={() => setLeadDialogOpen(false)}
        customer={leadDetails}
        onOpenCustomer={(customerId) => openCustomerQuick(customerId)}
        onAssignLead={(customer) => {
          setLeadDialogOpen(false);
          setAssignCustomer(customer);
          setSelectedAssignees(customer?.AssignedTo || []);
          setAssignNote("");
          setNotifyAssignee(true);
          setAssignOpen(true);
        }}
      />

      {/* ✅ NEW: Action cube dialog */}
      <LeadActionCubeDialog
        open={actionCubeOpen}
        onClose={() => setActionCubeOpen(false)}
        customer={actionCubeLead}
        instanceUniqueId={instanceUniqueId}
        onOpenCustomer={(customerId) => openCustomerQuick(customerId)}
        onAssignLead={(customer) => {
          setActionCubeOpen(false);
          setAssignCustomer(customer);
          setSelectedAssignees(customer?.AssignedTo || []);
          setAssignNote("");
          setNotifyAssignee(true);
          setAssignOpen(true);
        }}
      />

      <Dialog
        open={Boolean(leaderboardDetail)}
        onClose={() => setLeaderboardDetail(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{leaderboardDetail?.label || "Leaderboard detail"}</DialogTitle>
        <DialogContent dividers>
          {leaderboardDetail?.rows?.length ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              {leaderboardDetail.rows.map((row) => (
                <Box
                  key={row.id || `${row.PromoStoreId}-${row.wooOrderId}`}
                  sx={{
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>
                    {row.wooOrderNumber || row.customerName || "Order"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {row.status || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total:</strong>{" "}
                    {row.total != null
                      ? `${Number(row.total).toFixed(2)} ${row.currency || ""}`.trim()
                      : "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date:</strong>{" "}
                    {row.dateCreated ? new Date(row.dateCreated).toLocaleString() : "—"}
                  </Typography>
                  {row.adminUrl && (
                    <Typography variant="body2">
                      <a href={row.adminUrl} target="_blank" rel="noreferrer">
                        Open in WordPress
                      </a>
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography>No orders found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaderboardDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Quick sales assignment */}
      <Dialog
        className="sales-operation-assign-lead-popup"
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="create-order-dialog-title" sx={{ fontWeight: 800 }}>
          Assign Lead
        </DialogTitle>

        <DialogContent className="create-order-dialog-content" dividers>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>
            Assign <strong>{assignCustomer?.businessName || assignCustomer?.emailAddress}</strong> to an internal rep.
          </Typography>

          {/* This popup is intentionally staged: selecting users only updates local
              dialog state. The shared OrderUserAssignment component still supports
              immediate/debounced saving everywhere else that passes onSave. */}
          <OrderUserAssignment
            orderId={null}
            initialAssignedUsers={assignCustomer?.AssignedTo || []}
            onChangeAssignedUsers={setSelectedAssignees}
          />

          <TextField
            label="Internal note (optional)"
            value={assignNote}
            onChange={(e) => setAssignNote(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2, mt: 2, background: "#EEE" }}
          />

          <FormControlLabel
            control={<Switch checked={notifyAssignee} onChange={(e) => setNotifyAssignee(e.target.checked)} />}
            label="Notify assignee by email"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={!assignCustomer?.id || !selectedAssignees.length || assignLoading}
            onClick={async () => {
              try {
                setAssignLoading(true);

                await axios.post(
                  `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/assign`,
                  {
                    customerId: assignCustomer.id,
                    assignedTo: selectedAssignees.map((u) => ({
                      id: Number(u.id),
                      role: u.role || "order-rep",
                    })),
                    note: assignNote,
                    notify: notifyAssignee,
                    instanceUniqueId,
                  },
                  { headers: { accessToken: localStorage.getItem("accessToken") } }
                );

                await fetchLeads({ page: leadsPage, rpp: leadsRpp, q: leadsQ });
                setAssignOpen(false);
              } catch (err) {
                console.error("Assign failed:", err);
                alert("Assign failed. Check console.");
              } finally {
                setAssignLoading(false);
              }
            }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InstanceDashboard;
